/**
 * Field mapper for EntityForm.
 *
 * Maps FieldConfig definitions to the appropriate base component and its
 * props, enabling the EntityForm.Field component to render the correct
 * input for each field type.
 *
 * @see Spec section 3.5 for the field-to-component mapping table.
 */

import type { ChangeEvent, ComponentType } from 'react';

import type {
  FieldConfig,
  FieldType,
  SelectFieldOptions,
  CheckboxFieldOptions,
  CheckboxGroupFieldOptions,
  RadioFieldOptions,
  DateFieldOptions,
  TimeFieldOptions,
  DateRangeFieldOptions,
} from '../types';

import { TextField } from '@components/base/inputs/TextField/TextField';
import { Select } from '@components/base/inputs/Select/Select';
import { Checkbox } from '@components/base/inputs/Checkbox/Checkbox';
import { CheckboxGroup } from '@components/base/inputs/Checkbox/CheckboxGroup';
import { RadioGroup } from '@components/base/inputs/Radio/RadioGroup';
import { DatePicker } from '@components/base/inputs/DatePicker/DatePicker';
import { TimePicker } from '@components/base/inputs/TimePicker/TimePicker';
import { DateRangePicker } from '@components/base/inputs/DatePicker/DateRangePicker';
import { FileUpload } from '@components/base/inputs/FileUpload/FileUpload';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Props that the EntityForm.Field component has already resolved from
 * the form state (value, onChange, error state, etc.).
 */
export interface FieldProps {
  /** Current field value */
  value: unknown;
  /** Change handler */
  onChange: (value: unknown) => void;
  /** Blur handler */
  onBlur: () => void;
  /** Whether the field has an error */
  error: boolean;
  /** Error message string */
  errorMessage: string | undefined;
  /** Whether the field is disabled */
  disabled: boolean;
  /** Whether the field is required */
  required: boolean;
  /** Field name */
  name: string;
  /** Field HTML id */
  id: string;
}

/**
 * The result of mapping a FieldConfig to a renderable component.
 */
export interface FieldMapping {
  /** The React component to render */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: ComponentType<any>;
  /** The props to spread onto the component */
  props: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// TextField type set
// ---------------------------------------------------------------------------

/**
 * Field types that render via the TextField component.
 * Each maps to a specific HTML input `type` attribute.
 */
const TEXT_FIELD_TYPES: ReadonlySet<FieldType> = new Set([
  'text',
  'number',
  'email',
  'password',
  'tel',
  'url',
]);

/**
 * Maps EntityForm field types to the HTML input type attribute used
 * by the TextField component.
 */
const FIELD_TYPE_TO_INPUT_TYPE: Partial<Record<FieldType, string>> = {
  text: 'text',
  number: 'number',
  email: 'email',
  password: 'password',
  tel: 'tel',
  url: 'url',
};

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Copies defined properties from a source object to a target, optionally
 * renaming keys via the `keyMap`.  Eliminates repetitive `if (x !== undefined)`
 * branches and the cognitive-complexity cost that comes with them.
 *
 * @param target  - The props object being built.
 * @param source  - The options object to read from (may be undefined).
 * @param keys    - Property names to copy (source key names).
 * @param keyMap  - Optional mapping of source-key to target-key for renames.
 */
function copyDefinedProps(
  target: Record<string, unknown>,
  source: Record<string, unknown> | undefined,
  keys: readonly string[],
  keyMap?: Readonly<Record<string, string>>,
): void {
  if (!source) return;
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined) {
      target[keyMap?.[key] ?? key] = value;
    }
  }
}

// ---------------------------------------------------------------------------
// Mapper functions per field-type group
// ---------------------------------------------------------------------------

/**
 * Convert a number input's raw string to the form value.
 *
 * A cleared (or not-yet-valid, e.g. a lone `-`) `<input type="number">`
 * reports `''`; that is a MISSING value (`null`, like a cleared date / time
 * picker), never `Number('')` = `0` -- otherwise a cleared required field
 * passes as 0 and a cleared optional one is submitted as 0. Any other string
 * is a number the browser already validated (`0`, negatives, decimals).
 */
function toNumberValue(raw: string): number | null {
  return raw.trim() === '' ? null : Number(raw);
}

/** Keys copied from NumberFieldOptions. */
const NUMBER_OPTION_KEYS = ['min', 'max', 'step', 'leftElement', 'rightElement'] as const;

/** Keys copied from TextFieldOptions (with `autocomplete` renamed to `autoComplete`). */
const TEXT_OPTION_KEYS = ['maxLength', 'minLength', 'leftElement', 'rightElement', 'autocomplete'] as const;
const TEXT_OPTION_KEY_MAP: Readonly<Record<string, string>> = { autocomplete: 'autoComplete' };

/**
 * Build props for TextField-based field types
 * (text, number, email, password, tel, url).
 */
function mapTextFieldProps(
  fieldConfig: FieldConfig,
  fieldProps: FieldProps,
): FieldMapping {
  const inputType =
    /* v8 ignore next -- defensive fallback: all current text field types are in the map */
    FIELD_TYPE_TO_INPUT_TYPE[fieldConfig.type] ?? 'text';
  const isNumber = fieldConfig.type === 'number';

  // Wrap onChange to extract value from the native input ChangeEvent.
  // TextField uses a native <input>, which fires onChange with a ChangeEvent.
  const wrappedOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    fieldProps.onChange(isNumber ? toNumberValue(raw) : raw);
  };

  const baseProps: Record<string, unknown> = {
    ...fieldProps,
    // A missing value (`null`: a cleared number, or a null loaded from the
    // API) renders as an empty input -- `value={null}` would make the native
    // input uncontrolled.
    value: fieldProps.value ?? '',
    onChange: wrappedOnChange,
    type: inputType,
    label: fieldConfig.label,
    placeholder: fieldConfig.placeholder,
    helperText: fieldConfig.helperText,
    size: fieldConfig.size,
    readOnly: fieldConfig.readOnly,
  };

  // Apply type-specific options via declarative key lists
  const opts = fieldConfig.options as Record<string, unknown> | undefined;
  copyDefinedProps(
    baseProps,
    opts,
    isNumber ? NUMBER_OPTION_KEYS as unknown as string[] : TEXT_OPTION_KEYS as unknown as string[],
    isNumber ? undefined : TEXT_OPTION_KEY_MAP,
  );

  // Email fields get autocomplete="email" by default
  if (fieldConfig.type === 'email' && baseProps['autoComplete'] === undefined) {
    baseProps['autoComplete'] = 'email';
  }

  return {
    component: TextField,
    props: baseProps,
  };
}

/** Keys copied from TextareaOptions. */
const TEXTAREA_OPTION_KEYS = ['rows', 'maxLength', 'resize'] as const;

/**
 * Build props for the textarea field type.
 *
 * The spec indicates textarea will use an internal TextArea adapter.
 * For now we map to TextField as a placeholder -- the component layer
 * will swap to the TextArea adapter once it is implemented.
 */
function mapTextareaProps(
  fieldConfig: FieldConfig,
  fieldProps: FieldProps,
): FieldMapping {
  // Wrap onChange to extract value from the native textarea ChangeEvent.
  const wrappedOnChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    fieldProps.onChange(e.target.value);
  };

  const baseProps: Record<string, unknown> = {
    ...fieldProps,
    onChange: wrappedOnChange,
    label: fieldConfig.label,
    placeholder: fieldConfig.placeholder,
    helperText: fieldConfig.helperText,
    size: fieldConfig.size,
    readOnly: fieldConfig.readOnly,
    // The TextArea adapter will interpret these:
    as: 'textarea',
  };

  copyDefinedProps(
    baseProps,
    fieldConfig.options as Record<string, unknown> | undefined,
    TEXTAREA_OPTION_KEYS as unknown as string[],
  );

  // Placeholder: use TextField until the TextArea component is created
  return {
    component: TextField,
    props: baseProps,
  };
}

/** Keys copied from SelectFieldOptions. */
const SELECT_OPTION_KEYS = ['searchable', 'placeholder'] as const;

/**
 * Build props for select and multiselect field types.
 */
function mapSelectProps(
  fieldConfig: FieldConfig,
  fieldProps: FieldProps,
): FieldMapping {
  const opts = fieldConfig.options as SelectFieldOptions | undefined;

  const baseProps: Record<string, unknown> = {
    ...fieldProps,
    label: fieldConfig.label,
    helperText: fieldConfig.helperText,
    size: fieldConfig.size,
    // Pass `required` so a config-required select renders the asterisk (the
    // `...fieldProps` spread already carries it, but it is restated here to keep
    // parity with the other mappers' explicit baseProps surface).
    required: fieldProps.required,
    multiple: fieldConfig.type === 'multiselect',
    options: opts?.items ?? [],
  };

  copyDefinedProps(
    baseProps,
    opts as unknown as Record<string, unknown> | undefined,
    SELECT_OPTION_KEYS as unknown as string[],
  );

  return {
    component: Select,
    props: baseProps,
  };
}

/**
 * Build props for the single checkbox field type.
 */
function mapCheckboxProps(
  fieldConfig: FieldConfig,
  fieldProps: FieldProps,
): FieldMapping {
  const opts = fieldConfig.options as CheckboxFieldOptions | undefined;

  // Wrap onChange to extract checked boolean from the native checkbox ChangeEvent.
  const wrappedOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    fieldProps.onChange(e.target.checked);
  };

  const baseProps: Record<string, unknown> = {
    id: fieldProps.id,
    name: fieldProps.name,
    checked: fieldProps.value === true,
    onChange: wrappedOnChange,
    onBlur: fieldProps.onBlur,
    error: fieldProps.error,
    disabled: fieldProps.disabled,
    required: fieldProps.required,
    label: opts?.checkboxLabel ?? fieldConfig.label,
    helperText: fieldConfig.helperText,
    size: fieldConfig.size,
  };

  return {
    component: Checkbox,
    props: baseProps,
  };
}

/**
 * Build props for the checkboxGroup field type.
 */
function mapCheckboxGroupProps(
  fieldConfig: FieldConfig,
  fieldProps: FieldProps,
): FieldMapping {
  const opts = fieldConfig.options as CheckboxGroupFieldOptions | undefined;

  const baseProps: Record<string, unknown> = {
    id: fieldProps.id,
    name: fieldProps.name,
    value: Array.isArray(fieldProps.value) ? fieldProps.value : [],
    onChange: fieldProps.onChange,
    error: fieldProps.error,
    errorMessage: fieldProps.errorMessage,
    disabled: fieldProps.disabled,
    required: fieldProps.required,
    label: fieldConfig.label,
    helperText: fieldConfig.helperText,
    size: fieldConfig.size,
    options: opts?.items ?? [],
  };

  copyDefinedProps(
    baseProps,
    opts as unknown as Record<string, unknown> | undefined,
    ['orientation'],
  );

  return {
    component: CheckboxGroup,
    props: baseProps,
  };
}

/**
 * Build props for the radio field type.
 */
function mapRadioGroupProps(
  fieldConfig: FieldConfig,
  fieldProps: FieldProps,
): FieldMapping {
  const opts = fieldConfig.options as RadioFieldOptions | undefined;

  const baseProps: Record<string, unknown> = {
    id: fieldProps.id,
    name: fieldProps.name,
    value: fieldProps.value,
    onChange: fieldProps.onChange,
    error: fieldProps.error,
    errorMessage: fieldProps.errorMessage,
    disabled: fieldProps.disabled,
    required: fieldProps.required,
    label: fieldConfig.label,
    helperText: fieldConfig.helperText,
    size: fieldConfig.size,
    options: opts?.items ?? [],
  };

  copyDefinedProps(
    baseProps,
    opts as unknown as Record<string, unknown> | undefined,
    ['orientation'],
  );

  return {
    component: RadioGroup,
    props: baseProps,
  };
}

/**
 * Applies date-related options (minDate, maxDate, displayFormat) to the base props.
 * Shared by date and dateRange field types.
 *
 * Note: minDate/maxDate require Date conversion, so they use a dedicated
 * transform instead of the generic `copyDefinedProps`.
 */
function applyDateOptions(
  baseProps: Record<string, unknown>,
  opts: DateFieldOptions | DateRangeFieldOptions | undefined,
): void {
  if (!opts) return;
  const transforms: Array<[string, string, (v: string) => unknown]> = [
    ['minDate', 'minDate', (v) => new Date(v)],
    ['maxDate', 'maxDate', (v) => new Date(v)],
    ['displayFormat', 'format', (v) => v],
  ];
  for (const [srcKey, destKey, transform] of transforms) {
    const value = (opts as Record<string, unknown>)[srcKey];
    if (value !== undefined) {
      baseProps[destKey] = transform(value as string);
    }
  }
}

/**
 * Build props for the date field type.
 */
function mapDatePickerProps(
  fieldConfig: FieldConfig,
  fieldProps: FieldProps,
): FieldMapping {
  const opts = fieldConfig.options as DateFieldOptions | undefined;

  const baseProps: Record<string, unknown> = {
    id: fieldProps.id,
    name: fieldProps.name,
    value: fieldProps.value,
    onChange: fieldProps.onChange,
    onBlur: fieldProps.onBlur,
    error: fieldProps.error,
    errorMessage: fieldProps.errorMessage,
    disabled: fieldProps.disabled,
    required: fieldProps.required,
    label: fieldConfig.label,
    placeholder: fieldConfig.placeholder,
    helperText: fieldConfig.helperText,
    size: fieldConfig.size,
  };

  applyDateOptions(baseProps, opts);

  return {
    component: DatePicker,
    props: baseProps,
  };
}

/**
 * Build props for the time field type.
 *
 * Unlike `date`, the value is a plain `HH:mm` string -- there is no Date
 * conversion, so this mapper is simpler. The only type-specific option is
 * `step` (seconds), forwarded verbatim to the native `<input type="time">`.
 */
function mapTimePickerProps(
  fieldConfig: FieldConfig,
  fieldProps: FieldProps,
): FieldMapping {
  const opts = fieldConfig.options as TimeFieldOptions | undefined;

  const baseProps: Record<string, unknown> = {
    id: fieldProps.id,
    name: fieldProps.name,
    value: fieldProps.value,
    onChange: fieldProps.onChange,
    onBlur: fieldProps.onBlur,
    error: fieldProps.error,
    errorMessage: fieldProps.errorMessage,
    disabled: fieldProps.disabled,
    required: fieldProps.required,
    label: fieldConfig.label,
    placeholder: fieldConfig.placeholder,
    helperText: fieldConfig.helperText,
    size: fieldConfig.size,
  };

  if (opts?.step !== undefined) {
    baseProps['step'] = opts.step;
  }

  return {
    component: TimePicker,
    props: baseProps,
  };
}

/**
 * Build props for the dateRange field type.
 */
function mapDateRangePickerProps(
  fieldConfig: FieldConfig,
  fieldProps: FieldProps,
): FieldMapping {
  const opts = fieldConfig.options as DateRangeFieldOptions | undefined;

  const baseProps: Record<string, unknown> = {
    id: fieldProps.id,
    name: fieldProps.name,
    value: fieldProps.value,
    onChange: fieldProps.onChange,
    error: fieldProps.error,
    errorMessage: fieldProps.errorMessage,
    disabled: fieldProps.disabled,
    required: fieldProps.required,
    label: fieldConfig.label,
    helperText: fieldConfig.helperText,
    size: fieldConfig.size,
  };

  applyDateOptions(baseProps, opts);

  return {
    component: DateRangePicker,
    props: baseProps,
  };
}

/** Keys copied from FileFieldOptions. */
const FILE_OPTION_KEYS = ['accept', 'multiple', 'maxSize', 'maxFiles'] as const;

/**
 * Build props for the file field type.
 */
function mapFileUploadProps(
  fieldConfig: FieldConfig,
  fieldProps: FieldProps,
): FieldMapping {
  const baseProps: Record<string, unknown> = {
    id: fieldProps.id,
    value: fieldProps.value,
    onChange: fieldProps.onChange,
    error: fieldProps.error,
    errorMessage: fieldProps.errorMessage,
    disabled: fieldProps.disabled,
    required: fieldProps.required,
    label: fieldConfig.label,
    helperText: fieldConfig.helperText,
  };

  copyDefinedProps(
    baseProps,
    fieldConfig.options as Record<string, unknown> | undefined,
    FILE_OPTION_KEYS as unknown as string[],
  );

  return {
    component: FileUpload,
    props: baseProps,
  };
}

// ---------------------------------------------------------------------------
// Mapper dispatch table
// ---------------------------------------------------------------------------

/**
 * Dispatch table mapping FieldType values to their mapper functions.
 * This avoids a large switch statement and makes it easy to add new types.
 */
const MAPPER_DISPATCH: Record<
  FieldType,
  (fieldConfig: FieldConfig, fieldProps: FieldProps) => FieldMapping
> = {
  text: mapTextFieldProps,
  number: mapTextFieldProps,
  email: mapTextFieldProps,
  password: mapTextFieldProps,
  tel: mapTextFieldProps,
  url: mapTextFieldProps,
  textarea: mapTextareaProps,
  select: mapSelectProps,
  multiselect: mapSelectProps,
  checkbox: mapCheckboxProps,
  checkboxGroup: mapCheckboxGroupProps,
  radio: mapRadioGroupProps,
  date: mapDatePickerProps,
  time: mapTimePickerProps,
  dateRange: mapDateRangePickerProps,
  file: mapFileUploadProps,
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Maps a FieldConfig and runtime field props to a renderable component
 * and its props.
 *
 * @param fieldConfig - The field configuration from the EntityForm config.
 * @param fieldProps  - Runtime props resolved from form state (value, onChange, etc.).
 * @returns An object containing the React component and its props.
 *
 * @example
 * ```ts
 * const { component: Component, props } = mapFieldToComponent(fieldConfig, fieldProps);
 * return <Component {...props} />;
 * ```
 */
export function mapFieldToComponent(
  fieldConfig: FieldConfig,
  fieldProps: FieldProps,
): FieldMapping {
  const mapper = MAPPER_DISPATCH[fieldConfig.type];
  return mapper(fieldConfig, fieldProps);
}

/**
 * Returns true if the given field type renders via the TextField component.
 * Useful for determining input-type semantics (e.g., text vs textarea).
 */
export function isTextFieldType(type: FieldType): boolean {
  return TEXT_FIELD_TYPES.has(type);
}
